import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useIsOnline } from '@/contexts/PresenceContext'
import { useAiChatPanel } from '@/contexts/AiChatPanelContext'
import { useUser } from '@/app/store/auth'
import { MessageSquare } from 'lucide-react'

interface UserNameWithPresenceProps {
    userId: number | null | undefined
    name: string
    className?: string
    showChatOption?: boolean
}

/**
 * Displays a username with an online/offline presence indicator.
 * - Green dot to the top-right of the name when online
 * - Hover tooltip shows online/offline status
 * - Optional "Start chat" link on hover (hidden for the logged-in user)
 */
export function UserNameWithPresence({
    userId,
    name,
    className = '',
    showChatOption = true,
}: UserNameWithPresenceProps) {
    const { t } = useTranslation()
    const online = useIsOnline(userId)
    const aiChatPanel = useAiChatPanel()
    const currentUser = useUser()
    const [hovered, setHovered] = useState(false)
    const wrapperRef = useRef<HTMLSpanElement>(null)
    const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({})

    const isCurrentUser = currentUser?.id != null && userId != null && currentUser.id === userId

    // Position tooltip so it stays on screen
    useEffect(() => {
        if (!hovered || !wrapperRef.current) return
        const rect = wrapperRef.current.getBoundingClientRect()
        const style: React.CSSProperties = { position: 'fixed', zIndex: 100 }

        // Vertical: prefer below, flip above if near bottom
        if (rect.bottom + 60 > window.innerHeight) {
            style.bottom = window.innerHeight - rect.top + 4
        } else {
            style.top = rect.bottom + 4
        }

        // Horizontal: prefer left-aligned, shift left if near right edge
        if (rect.left + 160 > window.innerWidth) {
            style.right = window.innerWidth - rect.right
        } else {
            style.left = rect.left
        }

        setTooltipStyle(style)
    }, [hovered])

    if (!name || name === '—') {
        return <span className={className}>{name || '—'}</span>
    }

    return (
        <span
            ref={wrapperRef}
            className={`relative inline-flex items-center gap-1 ${className}`}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <span>{name}</span>
            {/* Presence dot */}
            <span
                className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${
                    online ? 'bg-green-500' : 'bg-gray-300'
                }`}
                style={{ marginTop: -4, marginLeft: -2 }}
            />

            {/* Hover tooltip — positioned via fixed to avoid clipping */}
            {hovered && (
                <span
                    className="bg-popover border rounded-md shadow-md px-3 py-2 text-xs whitespace-nowrap"
                    style={tooltipStyle}
                >
                    <span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${online ? 'bg-green-500' : 'bg-gray-300'}`} />
                    {online ? t('common.online', 'Online') : t('common.offline', 'Offline')}
                    {online && showChatOption && userId && !isCurrentUser && (
                        <button
                            className="flex items-center gap-1 mt-1.5 pt-1.5 border-t text-blue-600 hover:text-blue-700 w-full text-left"
                            onClick={(e) => {
                                e.stopPropagation()
                                setHovered(false)
                                if (aiChatPanel?.open) {
                                    aiChatPanel.open(undefined, undefined)
                                }
                            }}
                        >
                            <MessageSquare className="h-3 w-3" />
                            {t('common.startChat', 'Start chat')}
                        </button>
                    )}
                </span>
            )}
        </span>
    )
}
